import { AbsoluteCenter, Card, CardBody, chakra, Text } from "@chakra-ui/react";
import { AppLoggedOutLayout } from "app/AppLoggedOutLayout";
import { signIn, useGetPathAfterLogin } from "features/auth/AuthUtils";
import { TokenObtainPairResponse } from "gen/model/tokenObtainPairResponse";
import loglevel from "loglevel";
import React, { useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { LoadingState } from "shared/components/empty/LoadingState";
import { AppConfig } from "shared/config/AppConfig";
import { ArrowRightCircleIcon } from "shared/icons/ArrowRightCircleIcon";
import { rootRoute } from "shared/navigation/Routes";
import { isSafari } from "shared/utils/BrowserUtils";
import invariant from "shared/utils/TinyInvariant";
import { useEventListener } from "usehooks-ts";

const log = loglevel.getLogger("AuthIFrameClientPage");

interface MessageEventData {
  type?: string;
  source?: string;
  tokenData?: TokenObtainPairResponse;
}

/**
 * This is the "client" for exchanging auth tokens with a customer subdomain.
 * @see {@link https://www.notion.so/maybern/Customer-Subdomains-22e3767e933f4f68bf6d9405cbb20c01} for more information.
 * @see {@link AuthIFramePage} for the server.
 */
export const AuthIFrameClientPage: React.FC = () => {
  invariant(AppConfig.subdomains);

  const iframeUrl = new URL(
    rootRoute({}).auth({}).iframe({}).$,
    AppConfig.subdomains.authRootUrl,
  );

  const navigate = useNavigate();
  const getPathAfterLogin = useGetPathAfterLogin();

  const ref = useRef<HTMLIFrameElement>(null);

  const popupWindowRef = useRef<Window | null>();

  useEventListener("message", async (event) => {
    invariant(AppConfig.subdomains);
    if (!event.isTrusted) {
      log.warn("untrusted message", event);
      return;
    }
    const data: MessageEventData = event.data;
    if (isDevEventData(data)) {
      return;
    }
    const expectedOrigin = AppConfig.subdomains.authRootUrl.toString();
    const actualOrigin = new URL(event.origin).toString();
    if (actualOrigin !== expectedOrigin) {
      log.warn(
        "invalid origin",
        actualOrigin,
        "expected",
        expectedOrigin,
        "message",
        data,
      );
      return;
    }
    if (isSafari) {
      popupWindowRef.current?.close();
    }
    if (data.type === "mb-auth-logged-in" && data.tokenData) {
      await signIn(data.tokenData);
      navigate(getPathAfterLogin(), { replace: true });
    } else if (data.type === "mb-auth-logged-out") {
      const loginUrl = new URL(
        rootRoute({}).auth({}).login({}).$,
        AppConfig.subdomains.authRootUrl,
      );
      window.location.replace(loginUrl);
    }
  });

  // Safari does not allow iframes to access local storage from the domain they are hosted on,
  // so a popup window is used instead. See:
  // https://stackoverflow.com/questions/63922558/safari-localstorage-not-shared-between-iframes-hosted-on-same-domain
  const openLoginPopup = () => {
    popupWindowRef.current = window.open(iframeUrl.toString(), "_blank");
  };

  return (
    <AppLoggedOutLayout>
      {isSafari ? (
        <SafariLoginButton onClick={openLoginPopup} />
      ) : (
        <>
          <AbsoluteCenter>
            <LoadingState />
          </AbsoluteCenter>
          <chakra.iframe ref={ref} src={iframeUrl.toString()} display="none" />
        </>
      )}
    </AppLoggedOutLayout>
  );
};

const SafariLoginButton: React.FC<{
  onClick: () => void;
}> = ({ onClick }) => {
  const { t } = useTranslation(["auth"]);
  return (
    <AbsoluteCenter>
      <Card width="400px" height="190px" py="0" onClick={onClick}>
        <CardBody
          alignItems="center"
          justifyContent="center"
          display="flex"
          color="primary.900"
          cursor="pointer"
          _hover={{ bg: "primary.25" }}
          _active={{ bg: "primary.50" }}
        >
          <Text textStyle="h4">{t("auth:continueToLogin")}</Text>
          <ArrowRightCircleIcon ml="8px" w="24px" h="24px" />
        </CardBody>
      </Card>
    </AbsoluteCenter>
  );
};

const isDevEventData = (data: MessageEventData): boolean =>
  data.source === "react-devtools-content-script" ||
  data.source === "react-devtools-bridge" ||
  data.type === "intercom-snippet__ready";
